import React, { useEffect, useMemo, useRef, useState } from "react";
import CustomTimePicker from "./CustomTimePicker";

const safeNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const normalizeMedicationType = (value) => {
  return String(value || "")
    .trim()
    .toUpperCase();
};

const isUnitMedicationType = (type) => {
  return type === "COMPRIMIDO" || type === "CAPSULA";
};

const getMeasurementFamily = (type) => {
  return isUnitMedicationType(type) ? "UNIT" : "MG";
};

const getUnitFieldLabel = (type) => {
  if (type === "CAPSULA") return "Quantidade (cápsulas)";
  return "Quantidade (comprimidos)";
};

const getUnitPlaceholder = (type) => {
  if (type === "CAPSULA") return "Ex: 2 cápsulas";
  return "Ex: 2 comprimidos";
};

export default function DoseEditModal({
  open,
  onClose,
  editForm,
  setEditForm,
  selectableFlasks,
  getId,
  concLabelForFlask,
  isValidHHMM,
  writeLastDoseTime,
  onSave,
}) {
  const [medicationQuery, setMedicationQuery] = useState("");
  const initialFamilyRef = useRef(null);

  const currentStatus = String(
    editForm?.__derivedStatus ?? editForm?.status ?? ""
  ).toUpperCase();

  const canChangeMedication = currentStatus !== "APLICADA";

  const selectedFlask = useMemo(() => {
    const currentId = String(editForm?.flaskId || "");
    return (
      selectableFlasks.find((flask) => String(getId(flask)) === currentId) || null
    );
  }, [editForm?.flaskId, selectableFlasks, getId]);

  const selectedMedicationType = normalizeMedicationType(
    selectedFlask?.medicationType
  );

  useEffect(() => {
    if (!open) return;

    const baseType = normalizeMedicationType(selectedFlask?.medicationType);
    initialFamilyRef.current = baseType ? getMeasurementFamily(baseType) : null;

    if (selectedFlask) {
      const name = selectedFlask?.name || "Medicamento";
      const brand = selectedFlask?.brand ? ` • ${selectedFlask.brand}` : "";
      setMedicationQuery(`${name}${brand}`);
    } else {
      setMedicationQuery("");
    }
  }, [open, selectedFlask]);

  const filteredFlasks = useMemo(() => {
    const query = String(medicationQuery || "")
      .trim()
      .toLowerCase();

    const baseList = selectableFlasks.filter((flask) => {
      const flaskType = normalizeMedicationType(flask?.medicationType);
      const flaskFamily = getMeasurementFamily(flaskType);

      if (
        initialFamilyRef.current &&
        flaskFamily !== initialFamilyRef.current
      ) {
        return false;
      }

      if (!query) return true;

      const text = [
        flask?.name || "",
        flask?.brand || "",
        concLabelForFlask(flask) || "",
      ]
        .join(" ")
        .toLowerCase();

      return text.includes(query);
    });

    return baseList;
  }, [selectableFlasks, medicationQuery, concLabelForFlask]);

  const selectedMgPerUnit = safeNumber(selectedFlask?.mgPerUnit);
  const isUnitMedication = isUnitMedicationType(selectedMedicationType);

  const dosageInputValue = useMemo(() => {
    const dosageMg = safeNumber(editForm?.dosageMg);

    if (dosageMg == null) return "";

    if (isUnitMedication && selectedMgPerUnit && selectedMgPerUnit > 0) {
      return String(dosageMg / selectedMgPerUnit);
    }

    return String(editForm?.dosageMg ?? "");
  }, [editForm?.dosageMg, isUnitMedication, selectedMgPerUnit]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center p-3 pb-24 sm:p-6 bg-purple-950/40 backdrop-blur-sm overflow-y-auto">
      <div className="w-full max-w-md space-y-5 rounded-3xl bg-white p-5 sm:p-6 shadow-2xl mb-12 sm:mb-0 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b pb-3">
          <h2 className="font-lora text-base font-semibold text-[--primary-deep]">
            Editar dose
          </h2>

          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700"
            aria-label="Fechar"
            type="button"
          >
            ✕
          </button>
        </div>

        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
              Medicamento
            </label>

            {canChangeMedication && (
              <input
                type="text"
                className="input-base mb-2 h-11 w-full text-sm"
                placeholder="Digite para buscar o medicamento"
                value={medicationQuery}
                onChange={(e) => setMedicationQuery(e.target.value)}
              />
            )}

            <select
              className={`input-base h-11 w-full text-sm ${
                !canChangeMedication
                  ? "cursor-not-allowed bg-gray-100 text-gray-500"
                  : ""
              }`}
              value={editForm.flaskId}
              disabled={!canChangeMedication}
              onChange={(e) => {
                const nextFlaskId = e.target.value;
                const nextFlask =
                  selectableFlasks.find(
                    (flask) => String(getId(flask)) === String(nextFlaskId)
                  ) || null;

                const nextType = normalizeMedicationType(
                  nextFlask?.medicationType
                );
                const nextIsUnit = isUnitMedicationType(nextType);
                const nextMgPerUnit = safeNumber(nextFlask?.mgPerUnit);
                const currentDosageMg = safeNumber(editForm?.dosageMg);

                let nextDosageMg = editForm?.dosageMg ?? "";

                if (nextIsUnit) {
                  if (currentDosageMg != null && nextMgPerUnit && nextMgPerUnit > 0) {
                    const currentUnits =
                      isUnitMedication && selectedMgPerUnit && selectedMgPerUnit > 0
                        ? currentDosageMg / selectedMgPerUnit
                        : null;

                    nextDosageMg =
                      currentUnits != null ? String(currentUnits * nextMgPerUnit) : "";
                  } else {
                    nextDosageMg = "";
                  }
                } else if (isUnitMedication) {
                  nextDosageMg = "";
                }

                setEditForm((prev) => ({
                  ...prev,
                  flaskId: nextFlaskId,
                  dosageMg: nextDosageMg,
                }));
              }}
            >
              <option value="">Selecione…</option>

              {filteredFlasks.map((flask) => {
                const flaskId = String(getId(flask));
                const labelConc = concLabelForFlask(flask);

                return (
                  <option key={flaskId} value={flaskId}>
                    {flask?.name || "Medicamento"}
                    {flask?.brand ? ` • ${flask.brand}` : ""} ({labelConc})
                  </option>
                );
              })}
            </select>

            {!canChangeMedication && (
              <p className="text-xs text-amber-700">
                Esta dose já foi aplicada. O medicamento não pode mais ser
                alterado.
              </p>
            )}

            {canChangeMedication && initialFamilyRef.current === "UNIT" && (
              <p className="text-xs text-gray-500">
                Nesta edição, aparecem apenas medicamentos por unidade
                (comprimido/cápsula) para manter a consistência da dose.
              </p>
            )}

            {canChangeMedication && initialFamilyRef.current === "MG" && (
              <p className="text-xs text-gray-500">
                Nesta edição, aparecem apenas medicamentos dosados em mg/volume
                para manter a consistência da dose.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                Data
              </label>

              <input
                type="date"
                className="input-base h-11 w-full text-sm font-medium"
                value={editForm.dateLocal}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    dateLocal: e.target.value,
                  }))
                }
              />
            </div>

            <CustomTimePicker
              label="Hora"
              value={editForm.timeLocal}
              onChange={(time) => {
                setEditForm((prev) => ({
                  ...prev,
                  timeLocal: time,
                }));
                if (isValidHHMM(time)) {
                  writeLastDoseTime(time);
                }
              }}
            />
          </div>

          <div className="space-y-1">
            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
              {isUnitMedication
                ? getUnitFieldLabel(selectedMedicationType)
                : "Dosagem (mg)"}
            </label>

            <input
              type="number"
              step="0.01"
              className="input-base h-11 w-full text-sm"
              placeholder={
                isUnitMedication
                  ? getUnitPlaceholder(selectedMedicationType)
                  : "Ex: 5"
              }
              value={dosageInputValue}
              onChange={(e) => {
                const rawValue = e.target.value;

                if (rawValue === "") {
                  setEditForm((prev) => ({
                    ...prev,
                    dosageMg: "",
                  }));
                  return;
                }

                const numericValue = Number(rawValue);

                if (!Number.isFinite(numericValue)) {
                  setEditForm((prev) => ({
                    ...prev,
                    dosageMg: rawValue,
                  }));
                  return;
                }

                if (isUnitMedication && selectedMgPerUnit && selectedMgPerUnit > 0) {
                  setEditForm((prev) => ({
                    ...prev,
                    dosageMg: String(numericValue * selectedMgPerUnit),
                  }));
                  return;
                }

                setEditForm((prev) => ({
                  ...prev,
                  dosageMg: rawValue,
                }));
              }}
            />

            {isUnitMedication && selectedMgPerUnit && selectedMgPerUnit > 0 && (
              <p className="text-xs text-gray-500">
                Cada{" "}
                {selectedMedicationType === "CAPSULA" ? "cápsula" : "comprimido"}{" "}
                equivale a {selectedMgPerUnit} mg. O sistema salva internamente
                em mg para manter a compatibilidade.
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <input
              id="notifEdit"
              type="checkbox"
              checked={!!editForm.notificationActive}
              onChange={(e) =>
                setEditForm((prev) => ({
                  ...prev,
                  notificationActive: e.target.checked,
                }))
              }
            />

            <label htmlFor="notifEdit" className="text-sm text-gray-600">
              Lembrar-me desta medicação (notificações)
            </label>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onSave}
            className="btn-primary h-11 flex-1 font-bold"
            type="button"
          >
            Salvar
          </button>

          <button
            onClick={onClose}
            className="btn-secondary h-11 flex-1 font-bold"
            type="button"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}